import { useCallback, type FC, type PropsWithChildren } from 'react';
import { FrontendLayoutSettingsContext } from './FrontendLayoutSettingsContext';
import {
    EnumFrontendLayoutSetting,
    EnumFrontendLayoutSettingGroups,
    EnumFrontendLayoutSettingModuleName,
} from '@Model';

import { useSettings } from '@Hooks/Settings/useSettings';
import { PERMISSIONS_API } from '@Constants/backendConstants';

export const FrontendLayoutSettingsProvider: FC<PropsWithChildren> = ({ children }) => {
    const { settings, updateSetting, updateSettings } = useSettings<
        EnumFrontendLayoutSetting,
        EnumFrontendLayoutSettingGroups,
        EnumFrontendLayoutSettingModuleName
    >(
        `${PERMISSIONS_API}/frontend_layout_settings`,
        EnumFrontendLayoutSettingGroups.TableLayout,
        EnumFrontendLayoutSettingModuleName.Customizations,
    );

    const getSetting = useCallback(
        (setting: EnumFrontendLayoutSetting) => settings.find((s) => s.setting_name === setting),
        [settings],
    );

    const getParsedSetting = useCallback(
        (setting: EnumFrontendLayoutSetting) => {
            const s = getSetting(setting);
            if (!s) {
                return {
                    data: null,
                    setting: undefined,
                };
            }

            return {
                data: s.value?.length ? JSON.parse(s.value) : null,
                setting: s,
            };
        },
        [getSetting],
    );

    return (
        <FrontendLayoutSettingsContext.Provider value={{ getSetting, getParsedSetting, updateSetting, updateSettings }}>
            {children}
        </FrontendLayoutSettingsContext.Provider>
    );
};
